import { motion } from 'framer-motion';
import { 
  SparklesIcon, 
  FolderIcon, 
  DocumentTextIcon, 
  PlusIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Dashboard = ({ user }) => {
  const stats = [
    {
      name: 'Total Boards',
      value: '12',
      change: '+4.75%',
      changeType: 'positive',
      icon: FolderIcon,
    },
    {
      name: 'Content Items',
      value: '2,847',
      change: '+54.02%',
      changeType: 'positive',
      icon: DocumentTextIcon,
    },
    {
      name: 'AI Generations',
      value: '1,234',
      change: '+12.05%',
      changeType: 'positive',
      icon: SparklesIcon,
    },
    {
      name: 'Active Users',
      value: '573',
      change: '-1.39%',
      changeType: 'negative',
      icon: UserGroupIcon,
    },
  ];

  const quickActions = [
    {
      name: 'Create New Board',
      description: 'Start organizing your content',
      href: '/boards',
      icon: FolderIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Generate AI Content',
      description: 'Create scripts and content',
      href: '/ai-generator',
      icon: SparklesIcon,
      color: 'bg-purple-500',
    },
    {
      name: 'Upload Content',
      description: 'Add videos, PDFs, or links',
      href: '/content',
      icon: DocumentTextIcon,
      color: 'bg-green-500',
    },
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'board_created',
      title: 'Created "YouTube Strategy" board',
      time: '2 hours ago',
      icon: FolderIcon,
    },
    {
      id: 2,
      type: 'ai_generated',
      title: 'Generated script for "AI Tools Review"',
      time: '4 hours ago',
      icon: SparklesIcon,
    },
    {
      id: 3,
      type: 'content_added',
      title: 'Added 3 competitor videos to analysis',
      time: '6 hours ago',
      icon: DocumentTextIcon,
    },
    {
      id: 4,
      type: 'ai_generated',
      title: 'Generated Instagram captions',
      time: '1 day ago',
      icon: SparklesIcon,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-8 text-white"
      >
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {user?.name || 'Creator'}! 👋
        </h1>
        <p className="text-primary-100 text-lg">
          Ready to create amazing content today? Let's see what you can build.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
            className="card hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <ArrowTrendingUpIcon className={`self-center flex-shrink-0 h-4 w-4 ${
                        stat.changeType === 'positive' ? 'text-green-500' : 'text-red-500 rotate-180'
                      }`} />
                      <span className="sr-only">
                        {stat.changeType === 'positive' ? 'Increased' : 'Decreased'} by
                      </span>
                      {stat.change}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="lg:col-span-2"
        >
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
              <PlusIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <Link
                    to={action.href}
                    className="group relative block p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className={`inline-flex p-3 rounded-lg ${action.color} text-white mb-4`}>
                      <action.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                      {action.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {action.description}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="lg:col-span-1"
        >
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
              <ClockIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100">
                      <activity.icon className="h-4 w-4 text-primary-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.time}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6">
              <Link
                to="/activity"
                className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                View all activity →
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Demo Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <SparklesIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Demo Mode Active
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                You're currently in demo mode with sample data. All statistics and activities shown are for demonstration purposes.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard; 